import { DateTime } from '@mode-switch/express';
import { Model } from '@mode-switch/model';
import { BasicChannel, ColorTarget, Encoding, EncodingResult, MarkType, VariableType } from '@mode-vegas/vegas';
import { ChartFacade, DragInfo, SelectedChartService } from '@mode/charts/contract';
import {
  ChartActions,
  ChartSwitchActions,
  TempActions,
  fromChartSelectors,
  peekRedoStack,
  peekUndoStack,
} from '@mode/charts/domain';
import { ChartDrillDown, EncodingHelpers, Flamingo, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { ofType } from '@mode/legacy/util-domain';
import { ViewDataModalActions } from '@mode/reports/domain';
import { ChartTypes, DataProfilingTypes } from '@mode/shared/contract-common';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade } from '@mode/switch-views/contract';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Actions } from '~/actions';
import { ChartDesignerActionType } from '~/actions/chart-designer/designer.actions';
import { withLatestSnapshot } from '~/lib/charts/chart-pipes';
import { ChartViewData } from '~/lib/charts/chart-view-data';
import { ChartDesignerSelectors } from '~/selectors/designer.selectors';
import { AppStore } from '~/types/main.types';
import { ExplorerActionType } from '../../actions/chart-designer/explorer.actions';
import { addReportFiltersToChartEncoding } from '../../lib/chart-encoding-helpers';
import { updateBigValueTrendLabel } from '../../lib/viz-format-helpers';
import { currentReportTokenSelector, reportFilterControlsSelector } from '../../reducers/selectors';
import { ExplorerSelectors } from '../../selectors/explorer.selectors';

const EXP_TOKEN = FLAMINGO_RESERVED_REFS.ChartExplorer;

export class ChartExplorerService implements SelectedChartService {
  public static $inject = [
    '$rootScope',
    'RxStoreService',
    'actions$',
    'ToastService',
    'AnalyticsService',
    'ChartFacade',
    'DataViewFacade',
  ];

  private _dragField: Readonly<DragInfo>; // singleton for storing the currently dragged field

  constructor(
    private $rootScope: angular.IRootScopeService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private chartFacade: ChartFacade,
    private dataViewFacade: DataViewFacade
  ) {
    // Explore effect
    this.actions$
      .pipe(
        ofType(
          ExplorerActionType.ExploreChart,
          ExplorerActionType.ExploreChartOnLoad,
          TempActions.Type.ExploreChartWithData
        ),
        withLatestSnapshot(this.store, 'chartToken'),
        withLatestFrom(
          this.dataViewFacade.model$,
          this.store.select(reportFilterControlsSelector),
          ({ action, snapshot, queryToken }, model, filters) => {
            const validFilters = Flamingo.getValidReportFilters(model, queryToken, filters);
            const view = Model.Views.find(model, queryToken);
            return {
              filters: validFilters,
              action,
              snapshot,
              queryToken,
              view,
            };
          }
        ),
        map(({ action, snapshot, view, filters }) =>
          ChartActions.makeCopyTemp({
            token: EXP_TOKEN,
            originalToken: action.chartToken,
            chart: {
              encoding: addReportFiltersToChartEncoding(view, snapshot.encoding, filters, true),
            },
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    // Generate new encoding and chart type for explore mark
    this.actions$
      .pipe(
        ofType(TempActions.Type.ExploreChartMark),
        withLatestSnapshot(this.store, 'chartToken'),
        withLatestFrom(
          this.dataViewFacade.model$,
          this.store.select(reportFilterControlsSelector),
          ({ action, snapshot, queryToken }, model, filters) => {
            const validFilters = Flamingo.getValidReportFilters(model, queryToken, filters);
            const view = Model.Views.find(model, queryToken);
            return {
              schemaModel: SwitchSchemaModel.make(model, queryToken),
              view,
              action,
              snapshot,
              filters: validFilters,
            };
          }
        ),
        map(({ action, snapshot, view, schemaModel, filters }) =>
          ChartActions.makeCopyTemp({
            token: EXP_TOKEN,
            originalToken: action.chartToken,
            chart: {
              encoding: ChartDrillDown.encode(
                schemaModel,
                addReportFiltersToChartEncoding(view, snapshot.encoding, filters, true),
                action.target
              ).encoding,
              chartType: ChartDrillDown.newChartType(snapshot.chartType, action.target),
            },
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    // View Data
    this.actions$
      .pipe(
        ofType(TempActions.Type.ExploreChartWithData, ExplorerActionType.ViewChartData),
        switchMap((action) =>
          combineLatest([
            this.store.selectNoEmpty(ExplorerSelectors.getQueryToken),
            this.store.selectNoEmpty(ExplorerSelectors.getSelectedChartEncoding),
            this.store.select(ExplorerSelectors.getSelectedChartTitle),
            this.dataViewFacade.model$,
          ]).pipe(
            first(),
            map(([queryToken, encoding, title, model]) => ({
              action,
              queryToken,
              encoding,
              title,
              model,
            }))
          )
        ),
        map(({ action, queryToken, encoding, title, model }) =>
          ViewDataModalActions.openModal({
            encoding: ChartViewData.encodingFromTarget(model, queryToken, encoding, action.target),
            seriesDetail: ChartViewData.encodingSeriesDetail(model, queryToken, encoding, action.target),
            queryToken,
            title,
          })
        )
      )
      .subscribe((action) => this.store.dispatch(action));

    // Keep explorer paging offset in sync with current value of explorer source
    this.actions$
      .pipe(
        ofType(ExplorerActionType.ExploreChart, ExplorerActionType.ExploreChartOnLoad),
        switchMap(
          (action) => this.store.select(fromChartSelectors.createPagingOffsetSelector(action.chartToken)),
          (action, paging) => ({ action, paging })
        ),
        switchMap(({ action, paging }) =>
          this.store.select(fromChartSelectors.createChartFormatSelector(action.chartToken)).pipe(
            first(),
            map((chartFormat) => ({
              action,
              paging,
              chartFormat,
            }))
          )
        ),
        filter((payload) => payload.paging !== null)
      )
      .subscribe(({ action, paging, chartFormat }) => {
        this.store.dispatch({
          type: ExplorerActionType.SetActivelyExploredPagingOffsetAndPageSize,
          chartToken: action.chartToken,
          pagingOffset: paging,
          pageSize: chartFormat?.flatTable?.general?.pageSize,
        });
      });

    // Track analytics for explore interactions
    this.actions$
      .pipe(
        ofType(ExplorerActionType.ExploreChart, ExplorerActionType.TrackExploreInteraction),
        withLatestFrom(this.store.select(currentReportTokenSelector))
      )
      .subscribe(([action, currentReportToken]) => {
        const event =
          action.type === ExplorerActionType.ExploreChart ? AnalyticsEvent.Name.ExploreClickExplore : action.event;
        const props = { ...action.props, report_id: currentReportToken };
        this.analyticsService.run(RunMethodType.Track, event, props);
      });
  }

  withLatestExplorerChart<T>(obs: Observable<T>) {
    return obs.pipe(
      withLatestFrom(this.store.select(ChartDesignerSelectors.getPanes), (action, designer) => ({ action, designer }))
    );
  }

  // Stub, the explorer service doesn't currently need to handle this this
  public updateTitle(chartToken: string, title: string) {}

  // Stub, the explorer service doesn't currently need to handle this this
  public updateBigValueDescription(chartToken: string, description: string) {}

  public dispatchEncodingUpdate(result: EncodingResult, pillAction?: ChartTypes.PillActionType) {
    this.store.dispatch(ChartActions.updateEncoding({ token: EXP_TOKEN, encoding: result.encoding }));
  }

  // TODO: Migrate to action/reducer
  public addFieldAndMakeDual(
    enc: Encoding,
    field: CompiledFormula,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.addFieldAndMakeDual(enc, field, channel, leftMark, rightMark);
    this.dispatchEncodingUpdate(newEncoding);
  }

  public moveFieldAndMakeDual(
    enc: Encoding,
    field: CompiledField,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ) {
    const newEncoding = EncodingHelpers.moveFieldAndMakeDual(enc, field, channel, leftMark, rightMark);
    this.dispatchEncodingUpdate(newEncoding);
  }

  public removeSort(source: string) {
    this.store.dispatch(ChartActions.removeSort({ token: EXP_TOKEN, formula: source }));
  }

  updateChartType(chartType: ChartTypes.ChartType) {
    this.store.dispatch(ChartActions.updateType({ token: EXP_TOKEN, chartType }));
  }

  updateIsAutoUpdateDisabled(disable: boolean) {
    this.store.dispatch(ChartActions.updateIsAutoUpdateDisabled({ token: EXP_TOKEN, isAutoUpdateDisabled: disable }));
  }

  manuallyApplyChanges(): void {
    this.store.dispatch(ChartActions.manuallyApplyChanges({ token: EXP_TOKEN }));
  }

  updateCaption(chartToken: string, caption: ChartTypes.CaptionInfo) {
    this.store.dispatch(ChartActions.updateCaption({ token: EXP_TOKEN, caption }));
  }

  // Chart Formatting Actions
  updateChartFormat(format: ChartTypes.ChartFormat) {
    this.store.dispatch(ChartActions.updateFormat({ token: EXP_TOKEN, format }));
  }

  updateTextFormat(formula: string, pane: ChartTypes.FieldPaneTypes, format: ChartTypes.TextFormat) {
    this.store.dispatch(ChartActions.updateFieldFormat({ token: EXP_TOKEN, pane, formula, format }));
  }

  updateAxisFormat(formula: string, varType: VariableType, format: ChartTypes.AxisFieldFormat) {
    this.store.dispatch(ChartActions.updateAxisFieldFormat({ token: EXP_TOKEN, varType, formula, format }));
  }

  updatePaneFormat(formula: string, varType: VariableType, format: ChartTypes.PaneFieldFormat) {
    this.store.dispatch(ChartActions.updatePaneFieldFormat({ token: EXP_TOKEN, varType, formula, format }));
  }

  updateHeaderFormat(formula: string, varType: VariableType, format: ChartTypes.HeaderFieldFormat) {
    this.store.dispatch(ChartActions.updateHeaderFieldFormat({ token: EXP_TOKEN, varType, formula, format }));
  }

  updateLegendFormat(formula: string, varType: VariableType, format: ChartTypes.LegendFieldFormat) {
    this.store.dispatch(ChartActions.updateLegendFieldFormat({ token: EXP_TOKEN, varType, formula, format }));
  }

  updateBigValueTrendLabel(label: string) {
    this.store
      .select(ExplorerSelectors.getSelectedChartFormat)
      .pipe(
        first(),
        map((format) => updateBigValueTrendLabel(format, label))
      )
      .subscribe((format) => this.updateChartFormat(format));
  }

  updateColorField(
    formula: string,
    varType: VariableType,
    colorPaletteToken = '',
    palette = [],
    series?: string,
    colorMap?: ChartTypes.ColorEntries,
    gradient?: ChartTypes.ContinuousGradient,
    unassignedColor?: string,
    colorTarget?: ColorTarget
  ) {
    this.store.dispatch(
      ChartActions.updateFieldColor({
        token: EXP_TOKEN,
        varType,
        colorPaletteToken,
        palette,
        formula,
        series,
        colorMap,
        gradient,
        unassignedColor,
        colorTarget,
      })
    );
  }

  updateTheme(
    theme: string,
    palette = '',
    custom = [],
    showAlert = true,
    colorMap?: {
      [seriesName: string]: string;
    }
  ) {
    this.store.dispatch(
      ChartActions.updateColors({
        token: EXP_TOKEN,
        palette: custom,
        colorPaletteToken: palette,
        showAlert,
        colorMap,
      })
    );
  }

  updateFacetSizes(facetSizes: ChartTypes.FacetSizeMap) {
    this.store.dispatch(ChartActions.updateFacetSizes({ token: EXP_TOKEN, facetSizes }));
  }

  public dispatchEncodingAndFormatUpdate(
    result: EncodingResult,
    facetSizes?: ChartTypes.FacetSizeMap,
    fieldFormats?: ChartTypes.FieldFormatMap,
    format?: Partial<ChartTypes.ChartFormat>,
    fieldColors?: ChartTypes.FieldColorMap,
    colorPaletteToken?: string
  ) {
    this.store.dispatch(
      ChartActions.updateEncodingAndFormat({
        token: EXP_TOKEN,
        encoding: result.encoding,
        facetSizes,
        fieldFormats,
        format,
        fieldColors,
        colorPaletteToken,
      })
    );
  }

  public dispatchReplaceChartHistory(encoding: Encoding | undefined, isAutoUpdateDisabled: boolean | undefined) {
    this.store.dispatch(
      ChartActions.replaceChartHistory({
        token: EXP_TOKEN,
        encoding,
        isAutoUpdateDisabled,
      })
    );
  }

  clearChart() {
    this.store.dispatch(ChartActions.clearChart({ token: EXP_TOKEN }));
  }

  clearSorts() {
    this.store.dispatch(ChartActions.clearSorts({ token: EXP_TOKEN }));
  }

  clearFilters() {
    this.store.dispatch(ChartActions.clearFilters({ token: EXP_TOKEN }));
  }

  clearFormatting() {
    this.store.dispatch(ChartActions.clearFormatting({ token: EXP_TOKEN }));
  }

  undo(_: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(EXP_TOKEN),
      (obs) => obs.pipe(filter((history) => peekUndoStack(history) != null)),
      (history) => {
        if (history.undoStack.length > 0) {
          return ChartActions.undoTemp({
            token: EXP_TOKEN,
            snapshot: peekUndoStack(history),
          });
        }
      }
    );
  }

  redo(_: string) {
    this.store.dispatchThunkPipe(
      fromChartSelectors.createHistorySelector(EXP_TOKEN),
      (obs) => obs.pipe(filter((history) => peekRedoStack(history) != null)),
      (history) => {
        if (history.redoStack.length > 0) {
          return ChartActions.redoTemp({
            token: EXP_TOKEN,
            snapshot: peekRedoStack(history),
          });
        }
      }
    );
  }

  swapAxes() {
    this.store.dispatch(ChartActions.swapAxes({ token: EXP_TOKEN }));
  }

  quickSort(descending: boolean) {
    this.store.dispatch(ChartActions.quickSort({ token: EXP_TOKEN, descending }));
  }

  drillDown(chartToken: string, target: ChartTypes.MarkTarget) {
    this.store.dispatch(ChartActions.drilldown({ token: EXP_TOKEN, target }));
  }

  changeHierarchy(token: string, forward: boolean) {
    this.store.dispatch(ChartActions.changeHierarchy({ token: EXP_TOKEN, forward }));
  }

  dynamicDrillDown(
    chartToken: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ): void {
    this.store.dispatch(
      ChartActions.dynamicDrillDown({ token: EXP_TOKEN, target, targetFieldId, drillToField, dateTruncUnit })
    );
  }

  public setOffset(offset: number) {
    this.store.dispatch({
      type: ChartDesignerActionType.SetPagingOffset,
      vizToken: EXP_TOKEN,
      offset: offset,
    });
  }

  minimizeChartPane() {
    this.store.dispatch({
      type: ExplorerActionType.MinimizeChartPane,
    });
  }

  maximizeChartPane() {
    this.store.dispatch({
      type: ExplorerActionType.MaximizeChartPane,
    });
  }

  minimizeDataPane() {
    this.store.dispatch({
      type: ExplorerActionType.MinimizeDataPane,
    });
  }

  maximizeDataPane() {
    this.store.dispatch({
      type: ExplorerActionType.MaximizeDataPane,
    });
  }

  startDrag(info: DragInfo) {
    this._dragField = info;
    this.chartFacade.startFieldDrag(info.field, info.source);
  }

  endDrag() {
    this._dragField = undefined;
    this.chartFacade.endFieldDrag();
  }

  get dragField() {
    return this._dragField;
  }

  get readonly(): boolean {
    return true;
  }

  getDataProfiling(data: DataProfilingTypes.DataProfilingChartDetails) {
    this.store.dispatch(
      ChartSwitchActions.getDataProfiling({
        token: data.token,
        cols: data.cols,
        referenceId: data.referenceId,
      })
    );
  }

  setPageSize(pageSize: number) {
    this.store.dispatch({
      type: ChartDesignerActionType.SetPageSize,
      vizToken: EXP_TOKEN,
      pageSize: pageSize,
    });
  }

  getBinSize(token: string, columnName: string) {
    this.store.dispatch(
      ChartSwitchActions.getBinSize({
        token,
        columnName,
      })
    );
  }

  updateBaseFormatDisplayName(formula: string, varType: VariableType, format: ChartTypes.BaseFieldFormat): void {
    this.store.dispatch(
      ChartActions.updateBaseFormatDisplayName({
        format,
        varType,
        formula,
        token: EXP_TOKEN,
      })
    );
  }
}
